import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useId,
  useRef,
  useSyncExternalStore,
} from 'react';

/** Which part of the palette an action belongs under. */
export const paletteGroups = ['goTo', 'call', 'find', 'me', 'session'] as const;
export type PaletteGroup = (typeof paletteGroups)[number];

export interface PaletteAction {
  readonly id: string;
  readonly label: string;
  readonly group: PaletteGroup;
  /** A row of `lib/keyboard/shortcut-map.ts`, so the palette shows the keys it also answers. */
  readonly shortcutId?: string;
  readonly run: () => void;
}

/**
 * The store behind the registry.
 *
 * A plain object with a subscription rather than React state, and that is the whole reason this file
 * is shaped the way it is: the provider's value has to be **stable for the life of the provider**.
 * If it changed whenever something published, every publishing effect would depend on a new object,
 * publish again, and the application would loop.
 *
 * `snapshot` returns the same array until something actually changes, which is what
 * `useSyncExternalStore` requires of it.
 */
function createStore() {
  const published = new Map<string, readonly PaletteAction[]>();
  const listeners = new Set<() => void>();
  let snapshot: readonly PaletteAction[] = [];

  const recompute = () => {
    snapshot = [...published.values()].flat();
    for (const listener of listeners) {
      listener();
    }
  };

  return {
    publish(key: string, actions: readonly PaletteAction[]): void {
      published.set(key, actions);
      recompute();
    },
    withdraw(key: string): void {
      if (published.delete(key)) {
        recompute();
      }
    },
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    read(): readonly PaletteAction[] {
      return snapshot;
    },
  };
}

type Store = ReturnType<typeof createStore>;

const RegistryContext = createContext<Store | undefined>(undefined);

/**
 * Where the screens put what they can do, so the palette can offer it.
 *
 * The palette lives in the shell and the actions live in the screens — the agent's transfer acts on
 * the call that screen is holding, and nothing in the shell knows about it. The alternatives were
 * both worse: a palette that reached into every feature to ask what it could do would import the
 * whole application, and one driven by a global bus would offer actions for screens that are not
 * mounted.
 *
 * So a screen **publishes while it is mounted** and withdraws when it goes. What the person sees is
 * then exactly what is on screen, which is also the authorisation story: a screen they cannot open
 * publishes nothing.
 */
export function CommandRegistryProvider({ children }: { readonly children: ReactNode }) {
  const store = useRef<Store>(undefined);
  store.current ??= createStore();
  return <RegistryContext.Provider value={store.current}>{children}</RegistryContext.Provider>;
}

/**
 * Publishes this component's actions for as long as it is mounted.
 *
 * What the effect depends on is the **signature** — the ids, labels, groups and keys — and not the
 * array, because a caller rebuilds its handlers every render and republishing on each one would
 * re-render the palette with it. The handlers are read through a ref when the person chooses, so
 * what runs is always the current render's.
 *
 * Outside a provider this does nothing rather than throwing: a component is allowed to be rendered
 * in a story or a test without the shell around it, and losing a palette entry there is not a
 * failure.
 */
export function useCommandActions(actions: readonly PaletteAction[]): void {
  const store = useContext(RegistryContext);
  const key = useId();
  const latest = useRef(actions);
  latest.current = actions;
  const signature = actions
    .map((action) =>
      [action.id, action.label, action.group, action.shortcutId ?? ''].join('\u0000'),
    )
    .join('|');

  // `signature` is not read in the effect's body: it is the trigger. A republish should happen when
  // what the person *sees* changes, and never because a parent re-rendered and handed over a new
  // array of the same actions.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the signature is the trigger, as above
  useEffect(() => {
    if (store === undefined) {
      return;
    }
    const entries = latest.current.map((action) => ({
      ...action,
      run: () => {
        const current = latest.current.find((candidate) => candidate.id === action.id);
        current?.run();
      },
    }));
    store.publish(key, entries);
    return () => store.withdraw(key);
  }, [key, signature, store]);
}

/** Everything on offer right now: the shell's actions and whatever the screens have published. */
export function useCommandActionList(): readonly PaletteAction[] {
  const store = useContext(RegistryContext);
  const empty = useRef<readonly PaletteAction[]>([]).current;
  return useSyncExternalStore(
    store?.subscribe ?? (() => () => undefined),
    store?.read ?? (() => empty),
  );
}
